import { createFileRoute } from "@tanstack/react-router";
import { Clock, MapPin } from "lucide-react";
import { Page, SectionEyebrow, CTAButton } from "@/components/SiteLayout";
import lessenVideo from "@/assets/lessen.mp4.asset.json";

export const Route = createFileRoute("/lessen")({
  head: () => ({
    meta: [
      { title: "Lessen — Hi Coach" },
      { name: "description", content: "Zo ziet een zondagochtend bij Hi Coach eruit: ontvangst, startkring, beweegparcours en eindkring." },
      { property: "og:title", content: "Lessen bij Hi Coach" },
      { property: "og:description", content: "De vier stappen van elke les, voor kinderen van 2 tot 6 jaar." },
      { property: "og:url", content: "/lessen" },
    ],
    links: [{ rel: "canonical", href: "/lessen" }],
  }),
  component: Lessen,
});

function Lessen() {
  const steps = [
    { c: "#FF0000", t: "De Ontvangst", d: "De coach staat bij de deur en begroet elk kind persoonlijk op ooghoogte. Geen grote zaal waar je kind zomaar naar binnen loopt. Gewoon een vertrouwd gezicht dat er al op staat te wachten. Jij bent de eerste lessen van harte welkom om mee de zaal in te komen." },
    { c: "#FF7F00", t: "De Startkring", d: "We beginnen rustig. Samen in de kring, met een prentenboek als vertrekpunt. Sem en Maya luisteren mee. Het verhaal dat we lezen wordt de rode draad van de hele les." },
    { c: "#00C851", t: "Het Beweegparcours", d: "Dan gebeurt de magie. Het verhaal verandert in een parcours. Klimmen, springen, balanceren, rollen. Jouw kind speelt het avontuur na zonder dat het doorheeft dat het aan het sporten is." },
    { c: "#1E90FF", t: "De Eindkring", d: "We sluiten rustig af. Terug in de kring, even bijkomen. En elk kind gaat naar huis met een stuk vers fruit." },
  ];
  return (
    <Page>
      <section className="pt-24 pb-12 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          <SectionEyebrow color="var(--rb-blue)">Lessen</SectionEyebrow>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
            Zo ziet een zondagochtend <span style={{ color: "var(--rb-blue)" }}>bij Hi Coach</span> eruit
          </h1>
          <div className="mt-6 space-y-4 text-lg text-muted-foreground">
            <p>
              Samen op avontuur, rennen met een lach, voor de mooiste rode wangetjes van de dag! We klimmen heel hoog en rollen in het rond, elke zondag weer fit en gezond!
            </p>
            <p>
              Onze groepen zijn klein met structureel 10 tot 12 kinderen en onze lessen duren een vol uur (inclusief rustige uitstroom en fruitmoment). De absolute limiet is maximaal 12 kinderen per coach. Vol is echt vol!
            </p>
            <p>
              Geen twee lessen zijn hetzelfde, maar de opbouw is altijd vertrouwd. Voor kinderen geeft die herkenbaarheid veiligheid. Voor jou als ouder geeft het rust. Je weet wat je kind kan verwachten, en je kind weet wat er komt.
            </p>
          </div>
        </div>
      </section>


      <section className="py-16 gradient-rainbow-soft">
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold">De vier stappen van elke les.</h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Onze lessen duren een uur inclusief wisseltijd voor een rustige uitstroom en een gezond fruitmoment:
          </p>
          <div className="mt-10 space-y-5">
            {steps.map((s, i) => (
              <div key={s.t} className="bg-white rounded-3xl p-6 md:p-8 shadow-md flex gap-6 items-start">
                <div className="h-16 w-16 shrink-0 rounded-2xl flex items-center justify-center text-white text-2xl font-extrabold shadow-lg" style={{ background: s.c }}>
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-2xl font-extrabold">{s.t}</h3>
                  <p className="mt-2 text-lg">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="mx-auto max-w-5xl px-6">
          <SectionEyebrow color="var(--rb-blue)">Lesrooster</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-extrabold">Voor welke leeftijd, en wanneer?</h2>
          <p className="mt-3 text-lg text-muted-foreground max-w-3xl">
            Hi Coach is er voor kinderen van 2 tot 6 jaar. De groepen worden ingedeeld op basis van geboortejaar zodat
            elk kind begeleid wordt op het niveau waar het nu staat. Peuters en kleuters sporten op hetzelfde tijdstip,
            in twee gescheiden zaaldelen van Sporthal Portland.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            <div className="rounded-3xl p-8" style={{ background: "#FFE5E5" }}>
              <h3 className="text-2xl font-extrabold">Peuters van 2 en 3 jaar</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold shadow-sm">
                  <Clock size={16} /> Zondag 09:30 – 10:30 uur
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold shadow-sm">
                  <MapPin size={16} /> Sporthal Portland, zaaldeel 1
                </span>
              </div>
              <p className="mt-4 text-lg">
                Jij doet gewoon mee. Samen ontdekken, samen bewegen. De gymzaal is nieuw en spannend, en jouw aanwezigheid
                geeft je kind het vertrouwen om te verkennen.
              </p>
            </div>
            <div className="rounded-3xl p-8" style={{ background: "#E0F1FF" }}>
              <h3 className="text-2xl font-extrabold">Kleuters van 4 tot 6 jaar</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold shadow-sm">
                  <Clock size={16} /> Zondag 09:30 – 10:30 uur
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold shadow-sm">
                  <MapPin size={16} /> Sporthal Portland, zaaldeel 2
                </span>
              </div>
              <p className="mt-4 text-lg">
                Kinderen gaan zelfstandig op pad met de coach. Ouders en verzorgers zijn van harte welkom op de
                zaalvloer om mee te doen of aan te moedigen.
              </p>
            </div>
          </div>
          <p className="mt-6 text-sm text-muted-foreground max-w-3xl">
            Beide groepen sporten op hetzelfde tijdstip: de zaal van Sporthal Portland wordt in tweeën gesplitst, zodat
            peuters en kleuters elk hun eigen afgeschermde zaaldeel hebben.
          </p>
        </div>
      </section>

      <section className="py-20 gradient-rainbow-soft">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold">Het hoogtepunt van het jaar.</h2>
          <p className="mt-5 text-lg">
            Na een heel seizoen vol bewegen, groeien en avontuur is het tijd voor iets bijzonders. Aan het einde van het
            jaar wordt jouw kind tijdens een feestelijke uitreiking een officiële Hi Coach Held.
          </p>
          <p className="mt-4 text-lg">
            Geen standaard certificaatje. Een echte huldiging, voor een kind dat het hele jaar heeft laten zien wat het in zich heeft.
          </p>
          <p className="mt-4 text-lg">
            Voor de kinderen is het de bekroning van hun avontuur. Voor jou als ouder is het het moment waarop je ziet
            hoever jouw kind is gekomen sinds die eerste zondagochtend.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold">Een blik in de zaal.</h2>
          <p className="mt-2 text-muted-foreground">Zo ziet een zondagochtend eruit.</p>
          <div className="mt-6 mx-auto max-w-sm rounded-3xl overflow-hidden shadow-xl bg-white p-2">
            <video
              src={lessenVideo.url}
              className="w-full h-auto rounded-2xl aspect-[3/4] object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-white text-center">
        <CTAButton />
      </section>
    </Page>
  );
}
